<template>
  <div>
    <b-row style="margin-top: 2em">
      <b-col>
        <b-card bg-variant="light">
          <b-row>
            <b-col>
              <h5 class="text-info">{{ $t("globals.credits") }}:</h5>
            </b-col>
            <b-col cols="2">
              <b-btn
                size="sm"
                class="float-right"
                variant="success"
                @click="showCreateModal = true"
                :disabled="!getUserBrandPermissions.credit_create"
              >
                {{ $t("globals.create_new") }}
              </b-btn>
            </b-col>
          </b-row>
          <b-row class="my-3">
            <b-col>
              <b-input-group>
                <b-form-input
                  v-model="filter"
                  :placeholder="$t('globals.type_to_search')"
                />
                <b-input-group-append>
                  <b-btn
                    variant="warning"
                    :disabled="!filter"
                    @click="filter = ''"
                  >
                    {{ $t("globals.clear") }}
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col cols="2" >
              <b-form-select v-model="perPage" :options="perPageOptions" />
            </b-col>
          </b-row>
          <b-table
            :items="credits"
            :fields="fields"
            :filter="filter"
            :current-page="currentPage"
            @filtered="currentPage = 1"
            :sortable = false
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            bordered
            small
            sort-icon-left
            
          >
            <template v-slot:cell(action)="row">
              <b-btn
                @click="openPdf(row.item.id)"
                size="sm"
                variant="info"
                class="mx-1"
              >
                .pdf
              </b-btn>
            </template>
          </b-table>
          <b-row>
            <b-col md="6" class="my-1">
              <b-pagination
                :total-rows="total"
                :per-page="perPage"
                v-model="currentPage"
                class="my-0"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      centered
      size="lg"
      :visible="showCreateModal"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="container-fluid">
        <b-row>
          <b-col>
            <h4 class="text-info">{{ $t("globals.new_credit") }}:</h4>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols='12' sm='6' lg='4'>
            <span>{{ $t("globals.item_name") }}:</span>
            <b-input v-model.number="newItemName"> </b-input>
          </b-col>
          <b-col cols='12' sm='6' lg='4'>
            <span>{{ $t("globals.enter_amount") }}:</span>
            <b-input-group>
              <b-input v-model.number="newCreditAmount" type="number">
              </b-input>
              <b-input
                :placeholder="
                  '- ' + currencyFormat(newCreditAmount, currency).toString()
                "
                disabled
              >
              </b-input>
            </b-input-group>
          </b-col>
          <b-col cols="12" sm='6' lg='4'>
            <span>{{ $t("globals.vat") }}:</span>
            <b-input
              v-model.number="newItemVat"
              type="number"
              :disabled="getUserSystem.toLowerCase() == 'cartracker'"
            >
            </b-input>
          </b-col>
          <b-col cols="12" sm='6' lg='4'>
            <span>{{ $t("globals.quantity") }}:</span>
            <b-input v-model.number="newItemQuantity" type="number">
            </b-input>
          </b-col >
          <b-col cols="12" sm='6' lg='4'>
            <span>{{ $t("globals.exact_code") }}:</span>
            <b-form-select
              v-model="newItemExactCodeId"
              :options="exactCodeOptions"
            />
          </b-col>
          <b-col>
            <b-btn
              variant="success"
              class="m-1  mt-4 float-right"
              @click="addNewCreditToTable"
              :disabled="addItemDisabled"
            >
              {{ $t("globals.add") }}
            </b-btn>
          </b-col>
        </b-row>
        <hr />
        <b-row>
          <b-table
            class="mt-3"
            bordered
            small
            :items="newCredits"
            :fields="newCreditsFields"
          >
            <template v-slot:cell(credit)="row">
              {{ "- " + currencyFormat(row.item.credit, currency) }}
            </template>
            <template v-slot:cell(total_price_ex_vat)="row">
              {{
                "- " +
                  currencyFormat(row.item.quantity * row.item.credit, currency)
              }}
            </template>
            <template v-slot:cell(total_price_with_vat)="row">
              {{
                "- " +
                  currencyFormat(
                    row.item.quantity *
                      ((row.item.credit * (100 + row.item.item_vat)) / 100),
                    currency
                  )
              }}
            </template>
          </b-table>
        </b-row>
        <hr />
        <b-row>
          <b-col>
            <b-btn
              style="float: right; margin: 0.3em;"
              @click="createCredit"
              variant="success"
              :disabled="!this.newCredits.length > 0"
            >
              {{ $t("globals.create") }}
            </b-btn>
            <b-btn
              style="float: left; margin: 0.3em;"
              @click="cancelModal"
              variant="warning"
            >
              {{ $t("globals.cancel") }}
            </b-btn>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import { currencyFormat } from "@/helpers/price";

export default {
  props: {
    customer:{
      type: Number,
      require: true
    },
    refresh: Function,
    currency: String,
  },
  data() {
    return {
      total:0,
      tableLoading: false,
      newCredits: [],
      exactCodeOptions: [],
      newCreditAmount: 0,
      newItemExactCodeId: 0,
      newItemName: "",
      newItemVat: 21,
      newItemQuantity: 1,
      newCreditsFields: [
        {
          key: "item_name",
          label: this.$t("globals.name")
        },
        {
          key: "credit",
          label: this.$t("globals.credit"),
          sortable: true
        },
        {
          key: "quantity",
          label: this.$t("globals.quantity")
        },
        {
          key: "item_vat",
          label: this.$t("globals.vat")
        },
        {
          key: "total_price_ex_vat",
          label: this.$t("globals.total")
        },
        {
          key: "total_price_with_vat",
          label: this.$t("globals.total_price_with_vat")
        }
      ],
      showCreateModal: false,
      credits: [],
      filter: null,
      currentPage: 1,
      sortBy: "created_at",
      sortDesc: true,
      perPage: 10,
      perPageOptions: [
        { text: "10 " + this.$t("globals.per_page"), value: 10 },
        { text: "20 " + this.$t("globals.per_page"), value: 20 },
        { text: "30 " + this.$t("globals.per_page"), value: 30 }
      ],
      fields: [
        {
          key: "id",
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.created"),
          sortable: true
        },
        {
          key: "total_price",
          label: this.$t("globals.total_price"),
          sortable: true
        },
        {
          key: "action",
          label: this.$t("globals.action")
        }
      ]
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleMutation", "simpleFetch"]),
    currencyFormat,
    fetchExactCodeOptions() {
      let data = {
        query: `
          query  {
            ExactCodes {
              value: id
              text: description
            }
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({data, this_ref: this}).then((res) => {
        if (res) {
          this.exactCodeOptions = res.ExactCodes;
        }
      }).finally(()=>{
        this.setShowLoadingModal(false)
      });
    },
    cancelModal() {
      this.newCreditAmount = 0;
      this.newItemExactCodeId = 0;
      this.newItemName = "";
      this.newItemVat = 21;
      this.newItemQuantity = 1;
      this.newCredits = [];
      this.showCreateModal = false;
    },
    openPdf(id) {
      window.open(
        `${this.getBackendUrl}/pdf_invoices/${id}.pdf?tmpl=0&locale=${this.getLocale}`,
        "_blank"
      );
    },
    addNewCreditToTable() {
      this.newCredits.push({
        credit: this.newCreditAmount,
        item_name: this.newItemName,
        item_vat: this.newItemVat,
        quantity: this.newItemQuantity,
        exact_code_id: this.newItemExactCodeId
      });
      this.newCreditAmount = 0;
      this.newItemName = "";
      this.newItemVat = 21;
      this.newItemQuantity = 1;
      this.newItemExactCodeId = 0;
    },
    fetchTotalCredits(){
      const data = {
        query: `query { Customer(id:${parseInt(this.customer)}){ credits_total }}`
      };
      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.total = res.Customer.credits_total;
          }
        })
        .finally(() => {
          this.setShowLoadingModal(false);
      });
    },
    fetchCredits() {
      const offset = (this.currentPage-1) * this.perPage;
      const sort = this.sortDesc ? 'desc' : 'asc'
      const data = {
        query: `
          query {
            Customer(id:${parseInt(this.customer)}){ 
              creditsPagination (
                sortBy:"${this.sortBy}", 
                sort:"${sort}"
                limit: ${parseInt(this.perPage)},
                offset: ${parseInt(offset)}
              ){
                id
                total_price
                created_at
              }
            }
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.credits = res.Customer.creditsPagination.map(credit => {
              return {
                id: credit.id,
                total_price: this.currencyFormat(
                  credit.total_price,
                  this.currency
                ),
                created_at: credit.created_at.substring(0, 10)
              };
            });
          }
        })
        .finally(() => {
          this.setShowLoadingModal(false);
        });
    },
    createCredit() {
      const data = {
        query: `
          mutation CreditInvoiceCreate ($credit_invoice: CreditInvoiceInput) {
            res: CreditInvoiceCreate (credit_invoice: $credit_invoice)
          }
        `,
        variables: {
          credit_invoice: {
            customer_id: parseInt(this.$route.params.id),
            credit_items: this.newCredits
          }
        }
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.cancelModal();
        this.fetchCredits();
      });
    }
  },
  computed: {
    ...mapGetters([
      "getBackendUrl",
      "getLocale",
      "getUserSystem",
      "getUserBrandPermissions"
    ]),
    addItemDisabled() {
      return !(
        this.newItemName.length > 0 &&
        this.newCreditAmount > 0 &&
        this.newItemVat >= 0 &&
        this.newItemQuantity > 0 &&
        this.newItemExactCodeId > 0
      );
    }
  },
  mounted() {
    this.fetchTotalCredits();
    this.fetchExactCodeOptions();
  },
  watch:{
    total(n,o){
      if(this.total){
        this.fetchCredits();
      }
    },
    currentPage(n,o){
      this.fetchCredits();
    },
    perPage(n,o){
      this.fetchCredits();
    },
    sortBy(n,o){
      this.fetchCredits();
      // this.currentPage = 1; // To stay in the current page keep this commented, if after a sort it needs to go to the first page, uncomment
    },
    sortDesc(n,o){
      this.fetchCredits();
      // this.currentPage = 1; // To stay in the current page keep this commented, if after a sort it needs to go to the first page, uncomment
    }
  }
};
</script>
